import { useEffect, useMemo, useState } from 'react';
import Button from '../design/Button';
import './configurePlan.css';
import PurchaseSummary from './PurchaseSummary';
import { arrowRight, autodialIcon, browserCallingIcon, chevronLeft, fileIcon } from '../../utility-functions/assetLoader';
import colors from '../design/colors';
import { JService } from '../api-service/ApiService';
import { enqueueSnackbar } from 'notistack';
import { generateProformaInvoicePdf } from '../billing/GenerateProformaInvoicePDF';
import { getOrgURLParam } from '../../utility-functions';


const getPlanPriceData = (planName, plansData, billingCycle) => {
    for (let i = 0; i < plansData.length; i++) {
        let selectedPlan = plansData[i];
        if (
           selectedPlan?.name?.split("-")[0] === planName &&
           selectedPlan.billing_cycle === billingCycle?.toUpperCase()
        ) {
            let pricePerMonth =selectedPlan.discounted_price;
            if (billingCycle === "Quarterly") pricePerMonth /= 3;
            else if (billingCycle === "Annual") pricePerMonth /= 12;

            let viewer_license = selectedPlan?.viewer;
            return {
                price: Math.round(pricePerMonth * 100) / 100,
                currency:selectedPlan?.currency === "INR" ? "₹" : "$",
                name:selectedPlan.name,
                viewer_license
            };
        }
    }
    return { price: 0, currency: "$", name: "", viewer_license: {} };
};

const LicenseCounter = ({ value, onChange, min = 0 }) => (
    <div className="counter-controls">
        <button
            className="counter-btn"
            onClick={() => onChange(Math.max(min, value - 1))}
            disabled={value <= min}
            title={min == 5 ? "Need Minimum 5 licenses for INR plan" : ""}
        >
            −
        </button>
        <input
            type="number"
            className="counter-input"
            value={value}
            min={min}
            onChange={(e) => onChange(Math.max(min, parseInt(e.target.value) || 0))}
        />
        <button className="counter-btn" onClick={() => onChange(value + 1)}>
            +
        </button>
    </div>
);

const ConfigurePlan = ({ planName, billingCycle, plansData, onBack, onContinue, finalPurchaseData }) => {
    const { price: pricePerLicense, currency, name: plan_name, viewer_license } = getPlanPriceData(planName, plansData, billingCycle);
    const is_inr = currency === "₹";
    const [addonsList, setAddonListMain] = useState([]);
    const [licenseCount, setLicenseCount] = useState(1);
    const [viewerCount, setViewerCount] = useState(0);
    const [addonCounts, setAddonCounts] = useState(
        addonsList?.reduce((acc, addon) => ({ ...acc, [addon.id]: 0 }), {})
    );
    const [showLoader, setShowLoader] = useState(false);

    let viewerLicensePrice = useMemo(() => (
        viewer_license?.discounted_price ?? viewer_license?.price ?? 0
    ), [viewer_license]);

    const fetchAddonData = async () => {
        try {
            let res = await JService.get(`/api/v1/subscriptions/retrieve-applicable-addons/${getOrgURLParam()}`, {plan_name})
            if(!res?.success) throw new Error("Failed to fetch add-on data");
            let addons_data = res?.data;
            let filtered_addons = [];
            for (let addon of addons_data) {
                if (addon.name === "AUTODIAL") {
                    filtered_addons.push({...addon, 
                        icon: autodialIcon, 
                        name: 'Autodial',
                        description: 'Automatically dial a pre-set list of phone numbers, save time and manual effort.',
                        price: addon.discounted_price ?? addon.price,
                    });
                }
                if (addon.name === "BROWSER CALLING") {
                    filtered_addons.push({...addon, 
                        icon: browserCallingIcon, 
                        name: 'Browser calling',
                        description: 'Make and receive calls directly in your browser, no phone needed.',
                        price: addon.discounted_price ?? addon.price,
                    });
                }
            }

            setAddonListMain(filtered_addons);

        } catch(err) {
            console.error("Error fetching add-on data:", err);
            enqueueSnackbar("Failed to load add-on data. Please refresh the page.", { variant: "error" });
        }
    }

    useEffect(() => {
        fetchAddonData();
    }, []);

    useEffect(() => {
        if (!finalPurchaseData) return;
        setLicenseCount(finalPurchaseData.licenseCount || 1);
        setViewerCount(finalPurchaseData.viewerCount || 0);
        setAddonCounts(finalPurchaseData.addonCounts || {});
    }, [finalPurchaseData]);

    const updateAddonCount = (addonId, value) => {
        setAddonCounts((prev) => ({ ...prev, [addonId]: value }));
    };

    const onClick = () => {
        let final_purchase_data = {
            planName,
            billingCycle,
            pricePerLicense,
            viewerLicensePrice,
            licenseCount,
            viewerCount,
            addonsList,
            addonCounts,
            currency,
            fullPlanName: plan_name
        };


        onContinue(final_purchase_data);
    }
                        

    const downloadQuote = () => {
        setShowLoader(true);
        let proformaItems = [
            {
                type: "plan",
                quantity: licenseCount,
                plan: plan_name,
            }
        ];
        if(viewerCount > 0) {
            proformaItems.push({
                type: "viewer",
                quantity: viewerCount,
            });
        }

        for (let addon of addonsList) {
            if (addonCounts[addon.id] > 0) {
                proformaItems.push({
                    type: "addon",
                    quantity: addonCounts[addon.id],
                    feature_id: addon.id
                });
            };
        };

        const url = `/api/v1/subscriptions/proforma-invoice/${getOrgURLParam()}`;
        JService.post(url, JSON.stringify({ items: proformaItems }))
        .then((res) => {
            if (!res?.success || !res?.proforma_invoice) {
            throw new Error(
                res?.message || "Failed to generate proforma invoice",
            );
            }
            generateProformaInvoicePdf(res.proforma_invoice);
        })
        .catch((err) => {
            console.error("Proforma invoice error:", err);
            enqueueSnackbar(err?.message || "Could not generate proforma invoice", {
            variant: "error",
            });
        })
        .finally(() => {
            setShowLoader(false);
        });
    }

    return (
        <div className="configure-plan">
            <div className="configure-back" onClick={onBack}>
                {chevronLeft(14, 14, colors.nc_gray_800, { verticalAlign: 'middle'})} Back to plans
            </div>
            <h2 className="configure-heading">
                Configure your {planName} plan
            </h2>

            <div className="configure-layout">
                {/* Left column */}
                <div className="configure-left">
                    {/* License */}
                    <div className="configure-section">
                        <h3 className="section-title">License</h3>
                        <div className="license-card">
                            <div className="license-info">
                                <div className="license-name">
                                    {planName} plan - {billingCycle}
                                </div>
                                <div className="license-desc">
                                    Full access to all features included in the plan.
                                </div>
                            </div>
                            <div className="license-row">
                                <span className="license-price">
                                    {currency}{pricePerLicense}
                                    <span className="license-price-unit">/license/mo</span>
                                </span>
                                <div className="license-counter">
                                    <span className="counter-label">Licenses</span>
                                    <LicenseCounter
                                        value={licenseCount}
                                        onChange={setLicenseCount}
                                        min={1}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Viewer license */}
                    <div className="configure-section">
                        <div className="license-card">
                            <div className="license-info">
                                <div className="license-name">Viewer license</div>
                                <div className="license-desc">
                                    Access to all plan features except making and receiving calls.
                                </div>
                            </div>
                            <div className="license-row">
                                <span className="license-price">
                                    {currency}{viewerLicensePrice}
                                    <span className="license-price-unit">/license/mo</span>
                                </span>
                                <div className="license-counter">
                                    <span className="counter-label">Licenses</span>
                                    <LicenseCounter
                                        value={viewerCount}
                                        onChange={setViewerCount}
                                        min={0}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Add-ons */}
                    <div className="configure-section">
                        <h3 className="section-title">Add-ons</h3>
                        {addonsList.map((addon) => (
                            <div key={addon.id} className="license-card">
                                <div className="addon-header">
                                    {addon.icon()}
                                    <div className="license-info" style={{ marginBottom: 0 }}>
                                        <div className="license-name">{addon.name}</div>
                                        <div className="license-desc">{addon.description}</div>
                                    </div>
                                </div>
                                <div className="license-row">
                                    <span className="license-price">
                                        {currency}{Number(addon.price)}
                                        <span className="license-price-unit">/license/mo</span>
                                    </span>
                                    <div className="license-counter">
                                        <span className="counter-label">Licenses</span>
                                        <LicenseCounter
                                            value={addonCounts[addon.id] || 0}
                                            onChange={(val) => updateAddonCount(addon.id, val)}
                                            min={0}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right column */}
                <div className="configure-right">

                    <PurchaseSummary 
                        planName={planName}
                        billingCycle={billingCycle}
                        pricePerLicense={pricePerLicense}
                        viewerLicensePrice={viewerLicensePrice}
                        licenseCount={licenseCount}
                        viewerCount={viewerCount}
                        addonsList={addonsList}
                        addonCounts={addonCounts}
                        currency={currency}
                        isInr={is_inr}
                        HeaderButton={() => (
                        <Button
                            hierarchy="green"
                            buttonText="Continue to billing"
                            onClick={onClick}
                            styleOverride={{ width: '100%', marginBottom: '5px' }}
                            icon={{endIcon: arrowRight(colors.ic_white)}}
                        />
                    )}
                    />

                    <div className="finance-card">
                        <h3 className="finance-title">Need finance approval?</h3>
                        <p className="finance-desc">
                            Download a quote PDF to share with your finance team. Valid for 30
                            days.
                        </p>
                        <button onClick={downloadQuote} className="download-quote-btn" disabled={showLoader}>
                             {fileIcon()}{showLoader ? "Generating quote..." : "Download quote (PDF)"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfigurePlan;
